import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  UserPlus,
  GraduationCap,
  Target,
  Wallet,
  Heart,
  Globe2,
  Users,
  Trophy,
  Share2,
  ShieldCheck,
  CheckCircle2,
  Smartphone,
  ClipboardCheck,
  Megaphone,
  Search,
  Eye,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";
import AgentLeadForm from "@/components/politics/AgentLeadForm";

/* ============================================================= */
/*  1 — Hero                                                     */
/* ============================================================= */
function AgentsHero() {
  return (
    <section
      data-testid="agents-hero"
      className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
      <div
        className="absolute -top-32 right-[-12%] -z-10 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-40 left-[-12%] -z-10 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8 rsm-rise">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#1B1D1D]">
            <Sparkles size={12} className="text-[#7E20FF]" />
            RSM Network · Para agentes
          </div>
          <h1
            data-testid="agents-headline"
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.94] text-[#1B1D1D]"
          >
            Entre para a RSM Network e torne-se um{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#7E20FF]">
                agente secreto de marketing
              </span>
              <span className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-[#7E20FF]/15 rounded" />
            </span>
            .
          </h1>
          <p
            data-testid="agents-subhead"
            className="mt-8 max-w-3xl text-lg sm:text-xl leading-relaxed text-[#51565F]"
          >
            Receba missões digitais e presenciais, aprenda marketing na prática
            e seja remunerado pelo impacto que gera para marcas e campanhas.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#cadastro"
              data-testid="agents-hero-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#7E20FF] focus:ring-offset-2 focus:ring-offset-[#F5FAFB]"
            >
              Quero ser agente RSM
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="#como-funciona"
              data-testid="agents-hero-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white/70 backdrop-blur px-7 py-4 text-base font-medium text-[#1B1D1D] hover:border-[#7E20FF] hover:text-[#7E20FF] transition-all duration-300 ease-out"
            >
              Entender como funciona
            </a>
          </div>
          <p
            data-testid="agents-microcopy"
            className="mt-6 text-sm text-[#7D8188] max-w-xl"
          >
            Cadastro gratuito. Seleção e missões de acordo com o seu perfil.
          </p>
        </div>

        {/* Side: phone mockup with mission */}
        <div className="lg:col-span-4 lg:pl-4 rsm-rise rsm-rise-delay-2">
          <div className="relative mx-auto w-full max-w-[280px] aspect-[9/16] rounded-[36px] border border-[#1B1D1D]/15 bg-[#1B1D1D] shadow-xl p-3">
            <div className="h-full w-full rounded-[28px] bg-[#F5FAFB] p-4 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="grid h-6 w-6 place-items-center rounded-md bg-[#7E20FF] text-white text-[10px] font-bold">
                    R
                  </span>
                  <span className="font-display text-xs font-bold text-[#1B1D1D]">
                    Network
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#3ED383]/15 px-1.5 py-0.5 text-[8px] font-bold text-[#1d8b51]">
                  <span className="h-1 w-1 rounded-full bg-[#3ED383] animate-pulse" />
                  AGENTE
                </span>
              </div>

              <div className="mt-5 rounded-xl border border-[#7E20FF]/20 bg-white p-3.5 shadow-sm">
                <span className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#7E20FF]">
                  Nova missão · 50 pts
                </span>
                <p className="mt-1.5 text-[11px] leading-tight font-medium text-[#1B1D1D]">
                  Curtir e comentar a postagem oficial da marca X com a hashtag
                  #lancamentoX
                </p>
                <button className="mt-2.5 w-full rounded-full bg-[#7E20FF] py-1.5 text-[9px] font-bold tracking-wider uppercase text-white">
                  Aceitar
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-[9px]">
                <div className="rounded-lg bg-white p-2.5 border border-[#1B1D1D]/8">
                  <div className="text-[8px] uppercase tracking-wider text-[#7D8188] font-semibold">
                    Pontos
                  </div>
                  <div className="font-display text-base font-bold text-[#1B1D1D]">
                    2.430
                  </div>
                </div>
                <div className="rounded-lg bg-white p-2.5 border border-[#1B1D1D]/8">
                  <div className="text-[8px] uppercase tracking-wider text-[#7D8188] font-semibold">
                    Ranking
                  </div>
                  <div className="font-display text-base font-bold text-[#208BFF]">
                    #142
                  </div>
                </div>
              </div>

              <div className="mt-auto pt-3 text-center">
                <span className="text-[9px] text-[#7D8188]">
                  3 missões disponíveis
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  2 — Why                                                       */
/* ============================================================= */
function WhySection() {
  return (
    <section
      id="por-que"
      data-testid="agents-why"
      className="relative py-24 sm:py-32 border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Por que a RSM Network existe
          </span>
          <h2
            data-testid="agents-why-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Você passa o dia nas redes.{" "}
            <span className="text-[#7E20FF]">Por que não estar no jogo?</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-md">
            Marcas, influenciadores e campanhas movem milhões em atenção — e a
            maior parte das pessoas comuns só assiste. A RSM Network existe para
            trazer essa galera para dentro do fluxo, de forma organizada, ética
            e divertida.
          </p>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {[
            {
              eyebrow: "Realidade",
              title: "Atenção é dinheiro — mas quem ganha são poucos.",
              body: "Algumas mil pessoas no Brasil concentram o jogo da remuneração em redes. O resto vê tudo passar.",
            },
            {
              eyebrow: "Oportunidade",
              title: "Marcas precisam de gente real, não só de feed pago.",
              body: "Engajamento sincero, comentário verdadeiro e presença em ações — isso vale ouro para campanhas que não querem só comprar alcance.",
            },
            {
              eyebrow: "Visão RSM",
              title: "Qualquer pessoa pode ser a pessoa certa em uma campanha.",
              body: "Política, comercial, social — toda campanha precisa de pessoas comuns no lugar certo, na hora certa. A RSM Network organiza isso.",
            },
          ].map((b, i) => (
            <div
              key={b.title}
              data-testid={`agents-why-card-${i}`}
              className="group rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#7E20FF]/40"
            >
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#7E20FF]">
                {b.eyebrow}
              </span>
              <h3 className="mt-2 font-display text-xl font-semibold text-[#1B1D1D]">
                {b.title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#51565F]">
                {b.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  3 — How                                                       */
/* ============================================================= */
const HOW_PILLARS = [
  {
    icon: UserPlus,
    n: "01",
    title: "Entre para a Network",
    desc: "Crie seu perfil, informe interesses, sua cidade e as redes que você usa no dia a dia.",
    accent: "#7E20FF",
  },
  {
    icon: GraduationCap,
    n: "02",
    title: "Receba formação em pílulas",
    desc: "Microaulas rápidas sobre marketing, redes sociais, boas práticas e ética nas missões.",
    accent: "#208BFF",
  },
  {
    icon: Target,
    n: "03",
    title: "Cumpra missões pelo app",
    desc: "Missões digitais (curtir, comentar, responder, testar) e, em alguns casos, presenciais (eventos, lojas, cliente oculto).",
    accent: "#FA9F22",
  },
  {
    icon: Wallet,
    n: "04",
    title: "Ganhe pontos que viram dinheiro",
    desc: "Pontos por missão, rankings, ciclos de pagamento claros. Sem promessa exagerada de renda fácil.",
    accent: "#3ED383",
  },
];

function HowSection() {
  return (
    <section
      id="como-funciona"
      data-testid="agents-how"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Como funciona
          </span>
          <h2
            data-testid="agents-how-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Do cadastro à primeira missão paga, em quatro passos.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HOW_PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <article
                key={pillar.title}
                data-testid={`agents-how-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="grid h-11 w-11 place-items-center rounded-xl"
                    style={{
                      backgroundColor: `${pillar.accent}15`,
                      color: pillar.accent,
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <span
                    className="font-mono text-xs font-bold"
                    style={{ color: pillar.accent }}
                  >
                    {pillar.n}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {pillar.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {pillar.desc}
                </p>
                <span
                  className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                  style={{ background: pillar.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  4 — What is a mission                                         */
/* ============================================================= */
const MISSIONS = [
  {
    icon: Smartphone,
    title: "Curtir e comentar",
    desc: "Postagem oficial de um cliente em uma campanha estratégica. Briefing claro do que escrever — e o que não escrever.",
  },
  {
    icon: ClipboardCheck,
    title: "Responder uma pesquisa",
    desc: "Survey curto sobre hábitos de consumo, preferências ou impressão sobre uma marca / produto. Honesto e anônimo quando preciso.",
  },
  {
    icon: Megaphone,
    title: "Ação em local público",
    desc: "Participar de um flashmob comercial, evento de marca ou experiência sensorial — registrando provas no app.",
  },
  {
    icon: Eye,
    title: "Cliente oculto",
    desc: "Visitar uma loja ou testar um serviço, avaliando pontos pré-definidos e enviando notas estruturadas no app.",
  },
];

function MissionsSection() {
  return (
    <section
      id="missoes"
      data-testid="agents-missions"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            O que é uma missão
          </span>
          <h2
            data-testid="agents-missions-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Tarefas curtas, com briefing claro — não trabalho aleatório.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            Toda missão tem objetivo, regras, prazo e pontuação. Você sabe o que
            precisa fazer antes de aceitar — e só aceita o que quiser.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {MISSIONS.map((m, i) => {
            const Icon = m.icon;
            return (
              <article
                key={m.title}
                data-testid={`agents-mission-${i}`}
                className="group rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#7E20FF]/30"
              >
                <div className="flex items-start gap-5">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#7E20FF]/12 text-[#7E20FF] flex-shrink-0">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-[#1B1D1D]">
                      {m.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                      {m.desc}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  5 — Benefits                                                  */
/* ============================================================= */
const BENEFITS = [
  {
    icon: Wallet,
    title: "Renda extra com flexibilidade",
    desc: "Você escolhe quais missões aceitar conforme seu tempo e interesse — sem horário fixo, sem chefe direto.",
    accent: "#3ED383",
  },
  {
    icon: GraduationCap,
    title: "Aprendizado em marketing",
    desc: "A cada missão e pílula de conteúdo, você fica mais preparado para o mercado de comunicação e redes.",
    accent: "#208BFF",
  },
  {
    icon: Heart,
    title: "Pertencer a uma rede",
    desc: "Fazer parte de uma comunidade de agentes, com rankings, desafios e oportunidades reais.",
    accent: "#7E20FF",
  },
  {
    icon: Globe2,
    title: "Acesso a campanhas grandes",
    desc: "Desde campanhas políticas até lançamentos de grandes marcas — você pode estar em ações que viram referência.",
    accent: "#FA9F22",
  },
];

function BenefitsSection() {
  return (
    <section
      id="beneficios"
      data-testid="agents-benefits"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Benefícios de ser agente
          </span>
          <h2
            data-testid="agents-benefits-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            O que você ganha além dos pontos.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <article
                key={b.title}
                data-testid={`agents-benefit-${i}`}
                className="group rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${b.accent}15`,
                    color: b.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {b.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {b.desc}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  6 — Member-get-member                                         */
/* ============================================================= */
function ReferralSection() {
  return (
    <section
      data-testid="agents-referral"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden border-y border-[#1B1D1D]/8"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -top-32 left-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            <Share2 size={14} /> Member-get-member
          </span>
          <h2
            data-testid="agents-referral-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.98] text-[#F5FAFB]"
          >
            Monte o seu time RSM.
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#F5FAFB]/75">
            Agentes podem convidar amigos para formar o próprio time dentro da
            Network. Isso traz <span className="text-[#F5FAFB] font-medium">benefícios extras</span>
            : pontos adicionais por convites ativos, acesso a missões especiais
            de grupo e prioridade em campanhas selecionadas.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-[#F5FAFB]/85 max-w-2xl">
            {[
              "Qualidade do time importa mais que quantidade.",
              "Você ganha bônus quando seu time efetivamente engaja.",
              "Convites sem participação real não geram recompensa.",
            ].map((t, i) => (
              <li key={t} className="flex items-start gap-2.5">
                <CheckCircle2
                  size={15}
                  className="mt-0.5 text-[#7E20FF] flex-shrink-0"
                />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#7E20FF]/15 text-[#7E20FF]">
              <Users size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#F5FAFB]">
              Mecânica simples
            </h3>
            <ol className="mt-4 space-y-3 text-sm text-[#F5FAFB]/75">
              {[
                "Você gera um link no app.",
                "Amigos se cadastram via o seu link.",
                "Pontos extras quando eles começam a cumprir missões de verdade.",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-2.5">
                  <span className="text-xs font-mono text-[#7E20FF] mt-0.5">
                    0{i + 1}
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  7 — Ethics / selection                                        */
/* ============================================================= */
function EthicsSection() {
  return (
    <section
      data-testid="agents-ethics"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Segurança, ética e seleção
          </span>
          <h2
            data-testid="agents-ethics-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Aqui não é fábrica de engajamento falso.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            A RSM Network é séria. Tem regras, tem código de conduta e tem
            seleção — porque quem contrata as campanhas exige qualidade real,
            não números inflados.
          </p>

          <ul className="mt-8 space-y-3 max-w-2xl">
            {[
              "Sem spam, sem comportamento fake, sem violar políticas das plataformas.",
              "Ética obrigatória em avaliações e pesquisas — sua opinião é sua.",
              "Nem todos têm missões o tempo todo: depende do perfil, da demanda e do seu histórico.",
              "Transparência total nos pagamentos e nos critérios de seleção.",
            ].map((line, i) => (
              <li
                key={line}
                className="flex items-start gap-3 text-[15px] leading-relaxed text-[#1B1D1D]"
              >
                <ShieldCheck
                  size={18}
                  className="mt-0.5 text-[#208BFF] flex-shrink-0"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#1B1D1D] text-white">
              <Trophy size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
              O que acontece com quem joga sério
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
              Agentes consistentes sobem no ranking, ganham acesso a campanhas
              maiores, missões exclusivas e tarifas premium. Reputação dentro da
              Network é seu maior ativo.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  8 — How to start                                              */
/* ============================================================= */
const STEPS = [
  {
    n: "01",
    title: "Cadastrar interesse",
    desc: "Preencha o formulário com nome, e-mail, cidade e suas redes principais.",
  },
  {
    n: "02",
    title: "Receber convite",
    desc: "Quando abrir vaga compatível com seu perfil, enviamos o convite para o app.",
  },
  {
    n: "03",
    title: "Onboarding em pílulas",
    desc: "Microaulas rápidas para te preparar antes da primeira missão.",
  },
  {
    n: "04",
    title: "Começar a missionar",
    desc: "Missões compatíveis com seu perfil aparecem no app. Você escolhe quais aceitar.",
  },
];

function StartSection() {
  return (
    <section
      id="comecar"
      data-testid="agents-start"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Como começar
          </span>
          <h2
            data-testid="agents-start-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Quatro passos para a primeira missão paga.
          </h2>
        </div>

        <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((step, i) => (
            <li
              key={step.n}
              data-testid={`agents-start-step-${i}`}
              className="relative rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#7E20FF]/40"
            >
              <span className="font-mono text-sm font-semibold text-[#7E20FF]">
                {step.n}
              </span>
              <h3 className="mt-3 font-display text-lg font-semibold text-[#1B1D1D]">
                {step.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#51565F]">
                {step.desc}
              </p>
              {i < STEPS.length - 1 && (
                <span className="hidden lg:block absolute top-1/2 right-[-14px] -translate-y-1/2 text-[#1B1D1D]/15">
                  <ArrowRight size={20} />
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-14 text-center">
          <a
            href="#cadastro"
            data-testid="agents-start-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5"
          >
            Quero fazer parte da RSM Network
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  9 — FAQ                                                       */
/* ============================================================= */
const FAQS = [
  {
    q: "Quanto posso ganhar sendo agente?",
    a: "Depende do volume de missões aceitas, do seu perfil e da demanda das campanhas em curso. A RSM não promete renda fixa nem milagre — o ganho real é proporcional ao seu engajamento e à qualidade das suas entregas. Quando entrar no app, você verá o valor de cada missão antes de aceitar.",
  },
  {
    q: "Preciso ter muitos seguidores para participar?",
    a: "Não. A RSM Network não é programa de influenciador — é uma rede de pessoas comuns. O que importa é o seu perfil (interesses, cidade, redes que você usa) e a qualidade das suas entregas. Tem missão para perfis com 100 seguidores e para perfis com 50 mil.",
  },
  {
    q: "Missões presenciais são obrigatórias?",
    a: "Não. Você marca no cadastro se topa ou não missões presenciais. A maioria das missões é digital, e você só recebe ofertas compatíveis com o que você sinalizou.",
  },
  {
    q: "Como e quando recebo pelos pontos?",
    a: "Pontos viram dinheiro em ciclos de pagamento claros (tipicamente semanais ou quinzenais, dependendo do tipo de campanha). O app mostra seu saldo, missões pendentes de validação e a data do próximo ciclo.",
  },
  {
    q: "Posso indicar amigos e familiares?",
    a: "Sim — e a RSM incentiva isso. Você gera um link de convite no app e ganha pontos extras quando os convidados começam a cumprir missões de verdade. A regra é simples: convites que viram engajamento real geram bônus; convites que não engajam, não.",
  },
];

function AgentsFAQ() {
  return (
    <section
      id="faq"
      data-testid="agents-faq"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Perguntas frequentes
          </span>
          <h2
            data-testid="agents-faq-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            As dúvidas que todo agente novo tem.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 divide-y divide-[#1B1D1D]/10 rounded-2xl border border-[#1B1D1D]/10 bg-white"
          data-testid="agents-faq-accordion"
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-b-0 px-6"
              data-testid={`agents-faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#1B1D1D] hover:text-[#7E20FF] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#51565F] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="mt-10 text-center text-sm text-[#7D8188] max-w-2xl mx-auto">
          <Search size={14} className="inline-block mr-1 text-[#208BFF]" />
          Tem outra dúvida? Cadastre-se mesmo assim — vamos te explicar antes de
          você aceitar qualquer missão.
        </p>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  Page                                                          */
/* ============================================================= */
export default function AgentesPage() {
  return (
    <main
      data-testid="agentes-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />
      <AgentsHero />
      <WhySection />
      <HowSection />
      <MissionsSection />
      <BenefitsSection />
      <ReferralSection />
      <EthicsSection />
      <StartSection />
      <AgentsFAQ />
      <AgentLeadForm />
      <Footer />
    </main>
  );
}
